import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Receipt, Send } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const currency = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const CATEGORIES = ['travel', 'meals', 'supplies', 'equipment', 'other'];

export default function MyExpensesPage() {
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ category: 'travel', amount: '', currency: 'USD', description: '', date_incurred: '' });

  useEffect(() => { load(); }, []);
  const load = async () => {
    setLoading(true);
    try { const r = await axios.get(`${API}/api/employee-portal/expenses/claims`); setClaims(r.data.claims || r.data || []); }
    catch(e){ if(e?.response?.status !== 403) toast.error('Failed to load expenses'); }
    finally { setLoading(false); }
  };

  const submit = async () => {
    if (!form.amount || !form.description) { toast.error('Amount and description required'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/api/employee-portal/expenses/submit`, { ...form, amount: Number(form.amount) });
      toast.success('Expense submitted — awaiting manager approval.');
      setForm({ category: 'travel', amount: '', currency: 'USD', description: '', date_incurred: '' });
      load();
    } catch(e){ if(e?.response?.status !== 403) toast.error(e.response?.data?.detail || 'Failed to submit'); }
    finally { setBusy(false); }
  };

  const total = claims.reduce((a, c) => a + (c.amount || 0), 0);
  const pending = claims.filter(c => (c.status || '').toLowerCase() === 'pending').length;
  const approved = claims.filter(c => (c.status || '').toLowerCase() === 'approved').length;
  const statusColor = (s) => ({ approved: 'bg-emerald-100 text-emerald-800', rejected: 'bg-red-100 text-red-800', pending: 'bg-yellow-100 text-yellow-800' }[(s || '').toLowerCase()] || 'bg-gray-100 text-gray-700');

  return (
    <div className="min-h-screen p-6 bg-gray-50" data-testid="employee-expenses-page">
      <div className="max-w-5xl mx-auto space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Expenses</h1>
          <p className="text-gray-600 mt-1">Submit claims — approved amounts flow to your next paystub.</p>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          <Card><CardContent className="p-4"><div className="text-xs uppercase text-gray-500">Total submitted</div><div className="text-2xl font-bold">{currency(total)}</div></CardContent></Card>
          <Card><CardContent className="p-4"><div className="text-xs uppercase text-gray-500">Pending</div><div className="text-2xl font-bold text-yellow-600">{pending}</div></CardContent></Card>
          <Card><CardContent className="p-4"><div className="text-xs uppercase text-gray-500">Approved</div><div className="text-2xl font-bold text-emerald-600">{approved}</div></CardContent></Card>
        </div>

        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2"><Receipt className="h-5 w-5 text-yellow-600" /> Submit new expense</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <Label>Category</Label>
                <select data-testid="expense-category" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}
                  className="w-full h-10 border border-gray-300 rounded-md px-3">
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div><Label>Amount</Label><Input data-testid="expense-amount" type="number" step="0.01" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} placeholder="0.00" /></div>
              <div><Label>Date incurred</Label><Input data-testid="expense-date" type="date" value={form.date_incurred} onChange={e => setForm({ ...form, date_incurred: e.target.value })} /></div>
            </div>
            <div><Label>Description</Label><Textarea data-testid="expense-description" rows={2} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Business purpose, vendor, etc." /></div>
            <Button data-testid="expense-submit" onClick={submit} disabled={busy} className="bg-yellow-500 hover:bg-yellow-600 text-white">
              <Send className="h-4 w-4 mr-2" /> {busy ? 'Submitting…' : 'Submit expense'}
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>My claims</CardTitle></CardHeader>
          <CardContent>
            {loading ? <div className="text-gray-500">Loading…</div> :
             claims.length === 0 ? <div className="text-gray-500 italic">No expenses submitted yet.</div> :
             <div className="divide-y divide-gray-100">
               {claims.map((c, i) => (
                 <div key={i} className="py-3 flex items-center justify-between">
                   <div className="flex-1">
                     <div className="font-medium text-gray-900 capitalize">{c.category || 'expense'} · {currency(c.amount)}</div>
                     <div className="text-xs text-gray-500">{c.date_incurred || c.created_at || ''}</div>
                     {c.description && <div className="text-sm text-gray-600 mt-0.5 italic">&ldquo;{c.description}&rdquo;</div>}
                   </div>
                   <Badge className={statusColor(c.status)}>{c.status || 'pending'}</Badge>
                 </div>
               ))}
             </div>}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
